import React from 'react'
import { cn } from '@/lib/utils'
import { labelClass, labelStyle } from '@/lib/styleKit'
import { Eye, EyeSlash } from '@phosphor-icons/react'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      error,
      icon,
      iconPosition = 'left',
      id,
      'aria-describedby': ariaDescribedBy,
      disabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false)
    const autoId = React.useId()
    const inputId = id ?? autoId
    const errorId = `${inputId}-error`
    const describedBy =
      [error ? errorId : null, ariaDescribedBy].filter(Boolean).join(' ') || undefined

    const isPasswordType = type === 'password'
    const inputType = isPasswordType && showPassword ? 'text' : type
    const hasRightIcon = !!icon && iconPosition === 'right'
    const hasBothRightSlots = hasRightIcon && isPasswordType

    return (
      <div className="relative w-full">
        {label && (
          <label htmlFor={inputId} className={labelClass} style={labelStyle}>
            {label.includes('*') ? (
              <>
                {label.slice(0, label.indexOf('*'))}
                <span className="text-red-500 dark:text-red-400">*</span>
                {label.slice(label.indexOf('*') + 1)}
              </>
            ) : (
              label
            )}
          </label>
        )}

        <div className="relative group">
          {icon && iconPosition === 'left' && (
            <div
              className={cn(
                'absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-150',
                'text-muted-foreground dark:text-[#8a8f98]',
                'group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#a5b4fc]',
                error && 'text-red-500 dark:text-red-400'
              )}
            >
              {icon}
            </div>
          )}

          <input
            id={inputId}
            type={inputType}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            disabled={disabled}
            className={cn(
              'flex h-9 w-full rounded-md px-3 py-2 transition-colors duration-150',
              'border bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50',
              'hover:border-border/80 hover:bg-background',
              'focus:outline-none focus-visible:border-[#7170ff]/55',
              'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d]',
              'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
              'dark:focus-visible:border-[#7170ff]/75',
              'disabled:cursor-not-allowed disabled:opacity-50',
              icon && iconPosition === 'left' && 'pl-10',
              hasBothRightSlots ? 'pr-16' : (hasRightIcon || isPasswordType) && 'pr-10',
              error && 'border-red-500/60 focus-visible:border-red-500/70 dark:border-red-500/60 dark:focus-visible:border-red-500/70',
              className
            )}
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
            ref={ref}
            {...props}
          />

          {hasRightIcon || isPasswordType ? (
            <div className="absolute right-0 top-0 h-full flex items-center gap-1.5 pr-3">
              {hasRightIcon && (
                <div className="flex items-center justify-center text-muted-foreground dark:text-[#8a8f98] pointer-events-none">{icon}</div>
              )}
              {isPasswordType && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={disabled}
                  className="flex items-center justify-center h-5 w-5 text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-muted-foreground dark:disabled:hover:text-[#8a8f98]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeSlash className="h-4 w-4" weight="regular" />
                  ) : (
                    <Eye className="h-4 w-4" weight="regular" />
                  )}
                </button>
              )}
            </div>
          ) : null}
        </div>

        {error && (
          <p
            id={errorId}
            role="alert"
            className="mt-1.5 text-red-600 dark:text-red-400"
            style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
